import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import AddToDicIcon from "./assets/add-to-dic.svg";
import ExpandDownIcon from "../../../common/assets/dropdown-iterative-down.svg";
import ExpandUpIcon from "../../../common/assets/dropdown-iterative-up.svg";
import { FMSLogger } from "FMSLogger";
import { MISSION_DEPLOYMENT_ACTIONS } from "Utils/constants";
import { getStopsByCurrentSemanticMapDict } from "Slices/stops";
import { getVehiclesByCurrentSemanticMap } from "Slices/vehicles";
import { getVehiclesUpdateData } from "Slices/vehiclesState";
import { getCurrentFleetId } from "Slices/missionPlanner";
import { getVehicleTypes } from "Slices/vehicleTypes";
import { getMissionVersions } from "Slices/missionPlans";
import { MissionDeploy } from "../../../common/MissionDeploy";
import { MissionsModal } from "../../../MissionDetails/MissionsModal";
import { getDiffTime } from "Utils";

import API from "Utils/api";
import { MissionItemActions } from "../MissionItemActions/MissionItemActions";
import { MissionTimeline } from "./TimelineContainer";
import { VersionSelector } from "./VersionSelector";
import {
  DeployButtonContent,
  DeployText,
  DeployIconContainer,
  DeployButton,
  ExpandButton,
  DeploymentInfo,
  DeploymentLabel,
  DeploymentTime,
  ExpandIconContainer,
  MissionItemHeader,
  MissionItemContent,
  MissionInfoContainer,
  DeploymentInfoContainer,
  MissionItemContainer,
  MissionName,
  DeployModalContent,
  DeployModalBody,
  DeployModalFooter,
  ConfirmModalWrapper,
} from "./styles";
import {
  deepSnakeCaseKeys,
  sortCollectionByNumberField,
} from "Utils/collectionUtils";
import { EDIT_MISSION_MODAL, toggleModal } from "Slices/modals";

const missionDeploymentActionsLogger = FMSLogger.byPrefix(
  MISSION_DEPLOYMENT_ACTIONS
);

const RECENT_DEPLOY_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

interface MissionItemProps {
  missionData: {
    name: string;
    id: string;
    lastDeployed?: string | null;
    lastDeployedMs?: number | null;
    timeStamp?: string | null;
    updatedAt?: string;
    stops: any[];
    data: any[];
  };
  currentIndex: number;
  setEditData: (data: { index: number; data: any }) => void;
  deletePlanMissionAction: (id: string) => void;
  onDeleteVersion?: (data: {
    missionId: string;
    versionNumber: number;
  }) => void;
  savedMissionsDisabled: boolean;
  deployAction: () => void;
  deployPlanMissionAction: (data: any) => void;
  previousMode?: string;
  /** Controlled open state from parent (MissionList accordion). When provided,
   *  the item is in controlled mode and onExpand must also be provided. */
  isOpen?: boolean;
  /** Callback invoked when the expand button is clicked. Parent passes the
   *  mission id so it can manage a single-expand accordion state. */
  onExpand?: (id: string) => void;
}

export const getCurrentStops = (stops: any[], data: any[]): any[] => {
  // If stops array has items, use it
  if (stops && stops.length > 0) {
    return stops;
  }

  // If data is an array, use it
  if (Array.isArray(data)) {
    return data;
  }

  // If data is an object, convert it to array
  if (data && typeof data === "object") {
    return [data];
  }

  // Return empty array if nothing is available
  return [];
};

export const getStopName = (
  stopId: string,
  stopsObject: Record<string, any>
): string => {
  if (!stopId) {
    return "No Data";
  }
  return stopsObject[stopId]?.name || "No Data";
};

export const getDeployedAgo = (lastDeployed?: string | null): string => {
  if (!lastDeployed) return "-";
  return lastDeployed;
};

export const createEditData = (
  currentIndex: number,
  missionData: any
): { index: number; data: any } => {
  return {
    index: currentIndex,
    data: missionData,
  };
};

export const logMissionAction = (action: string, missionId: string): void => {
  missionDeploymentActionsLogger.debug(
    `mission item ${action} action for mission: ${missionId}`
  );
};

export const MissionItem = ({
  missionData,
  deletePlanMissionAction,
  onDeleteVersion,
  savedMissionsDisabled,
  deployPlanMissionAction,
  previousMode,
  isOpen: isOpenProp,
  onExpand,
}: MissionItemProps) => {
  const dispatch = useDispatch();
  const deployModalRef = useRef<HTMLDivElement>(null);
  const deployButtonRef = useRef<HTMLButtonElement>(null);
  // Uncontrolled fallback: used when the parent does not pass isOpen/onExpand.
  const [isOpenLocal, setIsOpenLocal] = useState(false);
  // Controlled mode when parent provides isOpen; uncontrolled otherwise.
  const isOpen = isOpenProp !== undefined ? isOpenProp : isOpenLocal;
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [selectedCycles, setSelectedCycles] = useState(1);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(
    null
  );
  const [selectedVehicleName, setSelectedVehicleName] = useState<string>("");
  const [selectedFleet, setSelectedFleet] = useState<string | null>(null);
  const [confirmOverwriteMissionData, setConfirmOverwriteMissionData] =
    useState<{
      text: string;
      confirmAction: () => void;
      cancelAction: () => void;
    } | null>(null);

  const { name, id, lastDeployed, timeStamp, updatedAt, stops, data } =
    missionData;
  const masterMissionId = (missionData as any).masterMissionId;
  const vehicleType = (missionData as any).vehicleType ?? null;
  const versionNumber = (missionData as any).versionNumber;

  const [selectedVersionId, setSelectedVersionId] = useState<string>(id);
  const [selectedVersionNumber, setSelectedVersionNumber] = useState<
    number | undefined
  >(versionNumber);
  const [selectedVersionStops, setSelectedVersionStops] = useState<
    any[] | null
  >(null);
  const [loadingVersionStops, setLoadingVersionStops] = useState(false);
  const versionFetchIdRef = useRef(0);
  const selectedVersionNumberRef = useRef(selectedVersionNumber);
  selectedVersionNumberRef.current = selectedVersionNumber;
  const stopsObject = useSelector(getStopsByCurrentSemanticMapDict);
  const vehicles = useSelector(getVehiclesByCurrentSemanticMap);
  const vehiclesUpdateData = useSelector(getVehiclesUpdateData);
  const currentFleetId = useSelector(getCurrentFleetId);
  const vehicleTypes = useSelector(getVehicleTypes);
  const cachedVersions = useSelector(
    masterMissionId ? getMissionVersions(masterMissionId) : () => null
  );

  const vehicleTypeName = vehicleType
    ? vehicleTypes.find((vt) => vt.id === vehicleType)?.name ?? null
    : null;

  // Sync selectedVersionId when missionData.id changes (e.g., after creating new version)
  useEffect(() => {
    setSelectedVersionId(id);
    setSelectedVersionNumber(versionNumber);
    setSelectedVersionStops(null);
    setLoadingVersionStops(false);
    // Invalidate any in-flight version fetch so stale responses are ignored
    versionFetchIdRef.current += 1;
  }, [id, versionNumber]);

  // After a version is deleted, the selected version may no longer exist in the
  // cache. Auto-select the first remaining version so the UI never references a
  // stale/deleted version, and fetch the new version's stops from the API.
  // NOTE: We intentionally depend only on cachedVersions (not selectedVersionNumber)
  // so this effect fires only when the cache changes (after deletion), not when
  // the user manually switches versions via the dropdown. We read the current
  // selectedVersionNumber from a ref to avoid stale closures.
  useEffect(() => {
    const currentVersionNum = selectedVersionNumberRef.current;
    if (!cachedVersions || currentVersionNum == null) return;
    const stillExists = cachedVersions.some((v: any) => {
      const vNum = v.versionNumber ?? v.version_number;
      return vNum === currentVersionNum;
    });
    if (!stillExists && cachedVersions.length > 0) {
      const next = cachedVersions[0] as any;
      const nextNum = next.versionNumber ?? next.version_number;
      setSelectedVersionId(next.id);
      setSelectedVersionNumber(nextNum);

      // Fetch the new version's stops so the timeline updates immediately.
      // Without this, the timeline falls back to sortedCurrentStops (the
      // deleted version's data from missionData props).
      // NOTE: This is an intentional second API call. The thunk already called
      // GET /versions/ to refresh the cache (version metadata only), but the
      // /versions/ endpoint doesn't include stop data. We need this separate
      // GET /missions/{id}/ call to load the full mission with stops.
      setLoadingVersionStops(true);
      const fetchId = ++versionFetchIdRef.current;
      API.get(`/missions/${next.id}/`)
        .then((response: any) => {
          if (fetchId !== versionFetchIdRef.current) return;
          setSelectedVersionStops(response.data.stops ?? []);
        })
        .catch(() => {
          if (fetchId !== versionFetchIdRef.current) return;
          setSelectedVersionStops(null);
        })
        .finally(() => {
          if (fetchId !== versionFetchIdRef.current) return;
          setLoadingVersionStops(false);
        });
    }
  }, [cachedVersions]);

  const currentStops = getCurrentStops(stops, data);

  const deploy = (assigneeData: any) => {
    const logObj = {
      funcName: "deploy",
      missionData,
      assigneeData,
      previousMode,
      selectedVersionId,
      selectedVersionNumber,
    };
    missionDeploymentActionsLogger.info(JSON.stringify(logObj));

    // Backend will fetch mission stops from DB using the mission ID
    // No need to send stops data from frontend
    deployPlanMissionAction({
      id: selectedVersionId,
      name: name,
      version: selectedVersionId,
      masterMissionId: masterMissionId,
      ...assigneeData,
      previous: previousMode,
    });

    setConfirmOverwriteMissionData(null);
    setShowDeployModal(false);
  };

  const deployWithOverwrite = (assigneeData: any) => {
    // Check if vehicle has mission in progress
    if (
      previousMode !== "Continue" &&
      vehiclesUpdateData &&
      vehiclesUpdateData[assigneeData.vehicle] &&
      vehiclesUpdateData[assigneeData.vehicle].mission_status === "IN_PROGRESS"
    ) {
      setConfirmOverwriteMissionData({
        text: "This vehicle has a mission in progress. Would you like to cancel the current mission and redeploy this one?",
        confirmAction: () => {
          missionDeploymentActionsLogger.debug("deploy mission with overwrite");
          deploy(assigneeData);
        },
        cancelAction: () => {
          missionDeploymentActionsLogger.debug(
            "cancel deploy mission with overwrite"
          );
          setConfirmOverwriteMissionData(null);
        },
      });
      return;
    }

    const missionLastDeployed = missionData.lastDeployedMs;
    if (
      missionLastDeployed &&
      missionLastDeployed < RECENT_DEPLOY_THRESHOLD_MS
    ) {
      setConfirmOverwriteMissionData({
        text: "This mission was deployed recently. Are you sure you want to deploy it again?",
        confirmAction: () => {
          missionDeploymentActionsLogger.debug("deploy mission with overwrite");
          deploy(assigneeData);
        },
        cancelAction: () => {
          missionDeploymentActionsLogger.debug(
            "cancel deploy mission with overwrite"
          );
          setConfirmOverwriteMissionData(null);
        },
      });
      return;
    }

    missionDeploymentActionsLogger.debug("deploy mission");
    deploy(assigneeData);
  };

  const getStopNameWithContext = (stopId: string) => {
    return getStopName(stopId, stopsObject);
  };

  const sortedCurrentStops = useMemo(
    () => sortCollectionByNumberField(currentStops, "order"),
    [currentStops]
  );

  const handleDeployModalClose = useCallback(() => {
    setShowDeployModal(false);
    setSelectedCycles(1);
    setSelectedVehicleId(null);
    setSelectedVehicleName("");
    setSelectedFleet(null);
  }, []);

  // Scroll the popover into view when it opens so it's visible for items near the bottom of the list
  useEffect(() => {
    if (!showDeployModal) return;
    // React populates refs before effects run, so the ref is guaranteed to be set here
    deployModalRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [showDeployModal]);

  // Close the deploy modal only when the accordion collapses an open item
  // (i.e. isOpen transitions from true → false). Checking the transition
  // rather than the static !isOpen condition prevents the modal from being
  // immediately dismissed when the deploy button is clicked on a collapsed item.
  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (wasOpen && !isOpen && showDeployModal) {
      handleDeployModalClose();
    }
  }, [isOpen, showDeployModal, handleDeployModalClose]);

  // Close the popover when the user clicks anywhere outside of it
  useEffect(() => {
    if (!showDeployModal) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        deployModalRef.current &&
        !deployModalRef.current.contains(e.target as Node) &&
        !deployButtonRef.current?.contains(e.target as Node) &&
        !confirmOverwriteMissionData
      ) {
        handleDeployModalClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDeployModal, handleDeployModalClose, confirmOverwriteMissionData]);

  const handleDeployAction = () => {
    logMissionAction("deploy", id);
    setShowDeployModal((prev) => {
      if (prev) {
        // Reset deploy state when closing via toggle, matching handleDeployModalClose
        setSelectedCycles(1);
        setSelectedVehicleId(null);
        setSelectedVehicleName("");
        setSelectedFleet(null);
      }
      return !prev;
    });
  };

  const handleVehiclePick = (vehicleId: string, cycles: string) => {
    setSelectedVehicleId(vehicleId);
    setSelectedFleet(null);
    setSelectedCycles(parseInt(cycles));
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    setSelectedVehicleName(vehicle?.name || "");
  };

  const handleFleetPick = (fleetId: string, cycles: string) => {
    // Guard against empty fleetId (e.g. when "Assign to first available" is unchecked)
    if (!fleetId) {
      setSelectedFleet(null);
      setSelectedCycles(parseInt(cycles));
      return;
    }
    setSelectedFleet(fleetId);
    setSelectedVehicleId(null);
    setSelectedVehicleName("");
    setSelectedCycles(parseInt(cycles));
  };

  const handleDeployConfirm = () => {
    if (selectedFleet) {
      // Fleet assignment is handled server-side: the backend picks an available vehicle,
      // so there is no specific vehicle to check for an in-progress mission or a
      // recent-deploy conflict. Skipping deployWithOverwrite is intentional here.
      deploy({ fleet: selectedFleet, loops: selectedCycles });
    } else if (selectedVehicleId) {
      deployWithOverwrite({
        vehicle: selectedVehicleId,
        loops: selectedCycles,
      });
    }
  };

  const handleExpand = () => {
    if (onExpand) {
      onExpand(id);
    } else {
      setIsOpenLocal((prev) => !prev);
    }
  };

  const editAction = () => {
    logMissionAction("edit", id);

    // Convert mission data to snake_case for the modal
    const missionStopsInSnakeCase = deepSnakeCaseKeys(sortedCurrentStops);

    // Open the edit mission modal with the converted data
    dispatch(
      toggleModal({
        type: EDIT_MISSION_MODAL,
        data: {
          id: id,
          name: name,
          timeStamp: timeStamp,
          stops: missionStopsInSnakeCase,
          vehicle_type: vehicleType,
        },
      })
    );
  };

  const deleteAction = () => {
    // Use version-specific delete if we have version info, fallback to old behavior
    if (selectedVersionNumber != null && masterMissionId && onDeleteVersion) {
      missionDeploymentActionsLogger.debug(
        `mission item delete action for master: ${masterMissionId}, version: ${selectedVersionNumber}`
      );
      onDeleteVersion({
        missionId: masterMissionId,
        versionNumber: selectedVersionNumber,
      });
    } else {
      logMissionAction("delete", selectedVersionId);
      deletePlanMissionAction(selectedVersionId);
    }
  };

  const handleVersionSelect = async (
    versionId: string,
    versionNumber: number
  ) => {
    setSelectedVersionId(versionId);
    setSelectedVersionNumber(versionNumber);
    missionDeploymentActionsLogger.debug(
      `Selected version ${versionNumber} (${versionId})`
    );

    if (versionId === id) {
      setSelectedVersionStops(null);
      setLoadingVersionStops(false);
      ++versionFetchIdRef.current;
      return;
    }

    setLoadingVersionStops(true);
    const fetchId = ++versionFetchIdRef.current;
    try {
      const response = await API.get(`/missions/${versionId}/`);
      if (fetchId !== versionFetchIdRef.current) return;
      setSelectedVersionStops(response.data.stops ?? []);
    } catch (error) {
      if (fetchId !== versionFetchIdRef.current) return;
      console.error("Failed to fetch version stops:", error);
      // Fallback to latest version stops
      setSelectedVersionStops(null);
    } finally {
      if (fetchId === versionFetchIdRef.current) {
        setLoadingVersionStops(false);
      }
    }
  };

  const deployedAgo = getDeployedAgo(lastDeployed);
  const templateUpdatedAgo = updatedAt ? getDiffTime(updatedAt) : "-";
  return (
    <>
      <MissionItemContainer isOpen={isOpen}>
        <MissionItemHeader>
          <MissionItemContent>
            <MissionName>{name}</MissionName>
            <MissionInfoContainer>
              <DeploymentInfoContainer>
                <DeploymentInfo>
                  <DeploymentLabel>Mission last updated: </DeploymentLabel>
                  <DeploymentTime>{templateUpdatedAgo}</DeploymentTime>
                </DeploymentInfo>
                <DeploymentInfo>
                  <DeploymentLabel>Mission last deployed: </DeploymentLabel>
                  <DeploymentTime>{deployedAgo}</DeploymentTime>
                </DeploymentInfo>
                {vehicleTypeName && (
                  <DeploymentInfo>
                    <DeploymentLabel>Vehicle type: </DeploymentLabel>
                    <DeploymentTime>{vehicleTypeName}</DeploymentTime>
                  </DeploymentInfo>
                )}
              </DeploymentInfoContainer>
            </MissionInfoContainer>
          </MissionItemContent>
          <DeployButton
            ref={deployButtonRef}
            onClick={handleDeployAction}
            disabled={savedMissionsDisabled}
            title="Deploy"
          >
            {isOpen && (
              <DeployButtonContent>
                <DeployText>Deploy</DeployText>
              </DeployButtonContent>
            )}
            <DeployIconContainer>
              <img src={AddToDicIcon} alt="Deploy" width={24} height={24} />
            </DeployIconContainer>
          </DeployButton>
          <VersionSelector
            missionId={id}
            masterMissionId={masterMissionId}
            onVersionSelect={handleVersionSelect}
            selectedVersion={selectedVersionNumber}
          />
          <ExpandButton
            onClick={handleExpand}
            title={isOpen ? "Collapse" : "Expand"}
          >
            <ExpandIconContainer>
              <img
                src={isOpen ? ExpandUpIcon : ExpandDownIcon}
                alt="Expand"
                width={24}
                height={24}
              />
            </ExpandIconContainer>
          </ExpandButton>
        </MissionItemHeader>
        {showDeployModal && (
          <DeployModalContent ref={deployModalRef}>
            <DeployModalBody>
              <MissionDeploy
                handleVehiclePick={handleVehiclePick}
                handleFleetPick={handleFleetPick}
                cancelAction={handleDeployModalClose}
                currentMissionDataStops={currentStops.map(
                  (stop) => stop.id || stop.name
                )}
                vehicleName={selectedVehicleName}
                vehicleId={selectedVehicleId}
                currentVehicles={vehicles}
                cycles={selectedCycles}
                onCyclesChange={setSelectedCycles}
                resetTrigger={showDeployModal ? 1 : 0}
                vehicleType={vehicleType}
                fleetId={currentFleetId}
              />
            </DeployModalBody>
            <DeployModalFooter>
              <button onClick={handleDeployModalClose}>Cancel</button>
              <button
                onClick={handleDeployConfirm}
                disabled={!selectedVehicleId && !selectedFleet}
              >
                OK
              </button>
            </DeployModalFooter>
          </DeployModalContent>
        )}
        {isOpen && (
          <>
            <MissionTimeline
              stops={
                selectedVersionStops
                  ? sortCollectionByNumberField(selectedVersionStops, "order")
                  : sortedCurrentStops
              }
              getStopName={getStopNameWithContext}
              loading={loadingVersionStops}
            />
            <MissionItemActions onEdit={editAction} onDelete={deleteAction} />
          </>
        )}
      </MissionItemContainer>

      {confirmOverwriteMissionData && (
        <ConfirmModalWrapper>
          <MissionsModal {...confirmOverwriteMissionData} />
        </ConfirmModalWrapper>
      )}
    </>
  );
};
